/**
 * Clean Search Modal Component
 * Handles search modal opening/closing and predictive search
 */

// RecentlyViewed class (copied from recently-viewed-products.js for compatibility)
// Adoric used to keep this list; it is gone, so the list is ours alone now.
class RecentlyViewed {
  static #STORAGE_KEY = 'viewedProducts';
  static #LEGACY_KEY = '_adoricRecentlyViewed'; // Adoric's, read once and dropped
  static #MAX_PRODUCTS = 4;

  static addProduct(productId) {
    if (!productId) {
      console.warn('📦 Cannot add product: productId is empty');
      return;
    }
    
    let viewedProducts = this.getProducts();
    viewedProducts = viewedProducts.filter(id => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    
    console.log('📦 Added product to recently viewed:', productId, 'Total:', viewedProducts.length);
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    console.log('🗑️ Cleared recently viewed products from storage');
  }

  static getProducts() {
    const stored = JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    if (stored.length) return stored;
    return this.#adoptLegacyProducts();
  }

  /**
   * Adoric kept its own list of viewed products. It no longer runs, but a
   * returning visitor still carries that list, so take it over once and
   * clear it; from then on the list is ours.
   */
  static #adoptLegacyProducts() {
    try {
      const legacy = localStorage.getItem(this.#LEGACY_KEY);
      if (!legacy) return [];
      const handles = JSON.parse(legacy)
        .slice(0, this.#MAX_PRODUCTS)
        .map(p => p.handle || p.id)
        .filter(Boolean);
      localStorage.removeItem(this.#LEGACY_KEY);
      if (handles.length) {
        localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(handles));
        console.log('📦 Took over', handles.length, 'products from the old list:', handles);
      }
      return handles;
    } catch (e) {
      console.error('Error reading the old viewed-products list:', e);
      return [];
    }
  }

  // Track product view from current page
  static trackCurrentProduct() {
    // Debounce tracking to prevent multiple rapid calls
    if (this._trackingTimeout) {
      clearTimeout(this._trackingTimeout);
    }
    
    this._trackingTimeout = setTimeout(() => {
      // Check if we're on a product page
      const productMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
      if (productMatch) {
        const productHandle = productMatch[1];

        // A product the shop hides is not added to the list. The page says so
        // itself, which is the only signal available here - this runs from a
        // URL and knows nothing about tags.
        if (document.querySelector('meta[name="product-hidden"]')) {
          console.log('\ud83d\udce6 Not tracking a hidden product:', productHandle);
          return;
        }
        
        // Always use handle for tracking - it's more reliable for fetching
        // Handles are URL-friendly and work better with search APIs
        console.log('📦 Tracking product view by handle:', productHandle);
        // Always add product - addProduct will handle deduplication
        this.addProduct(productHandle);
        const updatedProducts = this.getProducts();
        console.log('📦 Products in storage after add:', updatedProducts);
        
        // Clear cache in clean search if it exists, so new products show up
        if (window.cleanSearch) {
          window.cleanSearch.recentlyViewedCache = null;
          window.cleanSearch.recentlyViewedCacheTime = 0;
          console.log('📦 Cleared cache so new product will show on next modal open');
        }
      }
    }, 500); // 500ms debounce
  }
}

class CleanSearch {
  constructor() {
    this.modal = document.getElementById('clean-search-modal');
    this.input = document.getElementById('clean-search-input');
    this.clearButton = document.querySelector('[data-clear-search]');
    this.resultsContainer = document.querySelector('[data-results]');
    this.resultsGrid = document.querySelector('[data-results-grid]');
    this.viewAllContainer = document.querySelector('[data-view-all]');
    this.viewAllLink = document.querySelector('[data-view-all-link]');
    this.emptyContainer = document.querySelector('[data-empty-state]');
    this.recentlyViewedContainer = document.querySelector('[data-recently-viewed]');
    this.recentlyViewedProducts = document.querySelector('[data-recently-viewed-products]');
    this.recommendedProducts = document.querySelector('[data-recommended-products]');
    this.loadingIndicator = document.querySelector('.clean-search-loading');

    // Toggle button elements
    this.toggleButton = document.querySelector('[data-toggle-section]');
    this.toggleText = document.querySelector('[data-toggle-text]');

    this.minSearchLength = 2; // Reduced from 3 to 2 for faster search
    this.searchTimeout = null;
    this.abortController = null;
    this.totalResults = 0;
    this.isSearchActive = false; // Track when a search is active to prevent race conditions
    this.showingRecentlyViewed = true; // Track which section is visible

    // Cache for recently viewed products to avoid duplicate requests
    this.recentlyViewedCache = null;
    this.recentlyViewedCacheTime = 0;
    this.recentlyViewedLoading = false; // Prevent concurrent requests
    this.CACHE_DURATION = 30000; // 30 seconds cache

    // Cache for recommended products (to enable quick toggle)
    this.recommendedProductsCache = null;
    this.recommendedProductsLoaded = false;
    
    // Ensure initial state: search results hidden, empty state visible
    if (this.resultsContainer) {
      this.resultsContainer.hidden = true;
    }
    // Don't hide recently viewed container initially - let loadRecentlyViewedProducts decide
    // It will check for products and show/hide accordingly
    // if (this.recentlyViewedContainer) {
    //   this.recentlyViewedContainer.hidden = true;
    // }
    
    // Debug: Log initialization
    console.log('🔍 CleanSearch initialized');
    console.log('🔍 Modal:', this.modal);
    console.log('🔍 Recently viewed container:', this.recentlyViewedContainer);
    console.log('🔍 Recently viewed products:', this.recentlyViewedProducts);
    console.log('🔍 Recommended products:', this.recommendedProducts);
    console.log('🔍 Empty container:', this.emptyContainer);
    
    // Check if containers exist in DOM
    if (!this.recentlyViewedContainer) {
      console.error('❌ Recently viewed container not found in DOM!');
    }
    if (!this.recentlyViewedProducts) {
      console.error('❌ Recently viewed products element not found in DOM!');
    }
    
    this.init();
  }

  init() {
    // Open modal buttons
    document.querySelectorAll('[data-open-search]').forEach(button => {
      button.addEventListener('click', () => this.open());
    });

    // Close modal buttons
    document.querySelectorAll('[data-close-search]').forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    // Backdrop click to close
    if (this.modal) {
      const backdrop = this.modal.querySelector('.clean-search-modal__backdrop');
      const container = this.modal.querySelector('.clean-search-modal__container');
      
      if (backdrop) {
        backdrop.addEventListener('click', (e) => {
          if (e.target === backdrop) {
            this.close();
          }
        });
      }
      
      // Also close when clicking on modal itself (but not container)
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });
      
      // Prevent container clicks from closing modal
      if (container) {
        container.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }
    }

    // Clear search button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => this.clear());
    }

    // Input events
    if (this.input) {
      this.input.addEventListener('input', (e) => this.handleInput(e));
      this.input.addEventListener('keydown', (e) => this.handleKeydown(e));
    }

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Toggle button click handler
    if (this.toggleButton) {
      this.toggleButton.addEventListener('click', () => this.toggleSection());
    }
  }

  toggleSection() {
    console.log('🔄 toggleSection() called, currently showing:', this.showingRecentlyViewed ? 'recently viewed' : 'recommended');

    const recommendedSection = this.recommendedProducts?.closest('.clean-search-recommended');
    const recommendedTitle = recommendedSection?.querySelector('[data-recommended-title], .clean-search-recommended__title');
    const recentlyViewedTitle = this.recentlyViewedContainer?.querySelector('.clean-search-recently-viewed__title');
    const recentlyViewedProductsGrid = this.recentlyViewedProducts;

    if (this.showingRecentlyViewed) {
      // Switch to recommended products
      this.showingRecentlyViewed = false;

      // Hide recently viewed products grid (but keep the title with toggle visible)
      if (recentlyViewedProductsGrid) {
        recentlyViewedProductsGrid.hidden = true;
      }

      // Update the title to show "מומלצי החודש" when showing recommended
      const titleH4 = recentlyViewedTitle?.querySelector('h4');
      if (titleH4) {
        titleH4.textContent = 'מומלצי החודש';
      }

      // Show recommended section (products only, not its title)
      if (recommendedSection) {
        recommendedSection.hidden = false;
        recommendedSection.style.display = '';
      }
      // Keep the recommended title hidden (we use the recently viewed title area)
      if (recommendedTitle) {
        recommendedTitle.hidden = true;
      }

      // Update toggle button text
      if (this.toggleText) {
        this.toggleText.textContent = 'מוצרים שנצפו לאחרונה';
      }

      // Load recommended products if not already loaded
      if (!this.recommendedProductsLoaded) {
        this.loadRecommendedProductsForToggle();
      }

      console.log('🔄 Switched to recommended products');
    } else {
      // Switch back to recently viewed
      this.showingRecentlyViewed = true;

      // Hide recommended section
      if (recommendedSection) {
        recommendedSection.hidden = true;
        recommendedSection.style.display = 'none';
      }

      // Show recently viewed products grid
      if (recentlyViewedProductsGrid) {
        recentlyViewedProductsGrid.hidden = false;
      }

      // Update the title back to recently viewed
      const titleH4 = recentlyViewedTitle?.querySelector('h4');
      if (titleH4) {
        titleH4.textContent = 'מוצרים שנצפו לאחרונה';
      }

      // Update toggle button text
      if (this.toggleText) {
        this.toggleText.textContent = 'מומלצי החודש';
      }

      console.log('🔄 Switched to recently viewed');
    }
  }

  async loadRecommendedProductsForToggle() {
    // Load recommended products for the toggle (when switching from recently viewed)
    console.log('🔄 Loading recommended products for toggle...');

    const recommendedSection = this.recommendedProducts?.closest('.clean-search-recommended');
    const recommendedTitle = recommendedSection?.querySelector('[data-recommended-title], .clean-search-recommended__title');

    try {
      const recommended = await this.getRecommendedProducts();

      if (recommended.length > 0) {
        const validProducts = recommended.filter(p =>
          p.title &&
          p.title !== 'Product' &&
          p.title.length > 2 &&
          p.image &&
          p.image.length > 0
        );

        // Shuffle products
        for (let i = validProducts.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [validProducts[i], validProducts[j]] = [validProducts[j], validProducts[i]];
        }

        const displayProducts = validProducts.slice(0, 4);

        if (displayProducts.length > 0) {
          this.recommendedProductsCache = displayProducts;
          this.recommendedProductsLoaded = true;

          const html = displayProducts.map(product => `
            <a href="${product.url}" class="clean-search-product-card">
              <div class="clean-search-product-card__image-wrapper">
                ${product.image ? `<img src="${this.fixImageUrl(product.image)}" alt="${this.escapeHtml(product.title)}" class="clean-search-product-card__image" loading="lazy" onerror="this.style.display='none';">` : '<div class="clean-search-product-card__image-placeholder"></div>'}
              </div>
              <h3 class="clean-search-product-card__title">${this.escapeHtml(product.title)}</h3>
              ${product.price ? `<div class="clean-search-product-card__price">${this.escapeHtml(product.price)}</div>` : ''}
            </a>
          `).join('');

          this.recommendedProducts.innerHTML = html;

          if (recommendedTitle) {
            recommendedTitle.hidden = false;
          }
        }
      }
    } catch (error) {
      console.error('Error loading recommended products for toggle:', error);
    }
  }

  open() {
    if (!this.modal) return;
    
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Ensure search results are hidden when opening
    if (this.resultsContainer) {
      this.resultsContainer.hidden = true;
    }
    if (this.resultsGrid) {
      this.resultsGrid.innerHTML = '';
    }
    
    // Don't hide recently viewed here - let loadRecentlyViewedProducts() decide
    // It will check for products and show/hide accordingly
    // Reset loading flag so it can load fresh data
    this.recentlyViewedLoading = false;
    
    // Clear cache when opening modal to get fresh recently viewed products
    this.recentlyViewedCache = null;
    this.recentlyViewedCacheTime = 0;
    // Also clear recommended products cache to get fresh products
    this.recommendedProductsCache = null;
    this.recommendedProductsLoaded = false;
    // Reset toggle state
    this.showingRecentlyViewed = true;
    if (this.toggleText) {
      this.toggleText.textContent = 'מומלצי החודש';
    }
    // Reset the title text
    const recentlyViewedTitle = this.recentlyViewedContainer?.querySelector('.clean-search-recently-viewed__title h4');
    if (recentlyViewedTitle) {
      recentlyViewedTitle.textContent = 'מוצרים שנצפו לאחרונה';
    }
    // Make sure recently viewed products grid is visible (not hidden from previous toggle)
    if (this.recentlyViewedProducts) {
      this.recentlyViewedProducts.hidden = false;
    }
    console.log('📦 Cleared cache on modal open to get fresh products');
    
    // Clear any previous search results and show empty state
    // This will reload recently viewed and recommended products
    this.showEmptyState();
    
    // Focus input after animation
    setTimeout(() => {
      if (this.input) {
        this.input.focus();
      }
    }, 100);

    // If there's text in the input, perform search
    if (this.input?.value.trim()) {
      this.handleInput({ target: this.input });
    }
    // Note: loadRecommendedProducts() is already called in showEmptyState()
  }

  close() {
    if (!this.modal) return;
    
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    // Clear search
    this.clear();
  }

  isOpen() {
    return this.modal?.getAttribute('aria-hidden') === 'false';
  }

  clear() {
    if (this.input) {
      this.input.value = '';
    }
    this.updateClearButton();
    this.showEmptyState();
    // Note: showEmptyState() already calls loadRecommendedProducts()
  }

  handleInput(e) {
    const query = e.target.value.trim();
    this.updateClearButton();
    
    // Cancel previous search
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null; // Clear the controller after aborting
    }

    // Clear timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (query.length === 0) {
      // Show recommended products when input is cleared
      this.showEmptyState();
      // Note: showEmptyState() already calls loadRecommendedProducts()
      return;
    }

    if (query.length < this.minSearchLength) {
      // Show recommended products while typing (before 3 characters)
      this.showEmptyState();
      // Note: showEmptyState() already calls loadRecommendedProducts()
      return;
    }

    // Debounce search - perform search after user stops typing (reduced from 300ms to 150ms for faster response)
    this.searchTimeout = setTimeout(() => {
      // Wrap in try-catch to handle abort errors silently
      this.performSearch(query).catch(err => {
        // Silently ignore AbortError - it's expected when user types quickly
        if (err.name !== 'AbortError') {
          console.error('🔍 Search error:', err);
        }
      });
    }, 150);
  }

  handleKeydown(e) {
    if (e.key === 'Enter' && this.input.value.trim()) {
      e.preventDefault();
      this.submitSearch();
    }
  }

  async performSearch(query) {
    if (!query || query.length < this.minSearchLength) return;

    this.isSearchActive = true; // Mark search as active to prevent race conditions
    this.showLoading();

    // Create new abort controller
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    try {
      console.log('🔍 Performing search for:', query);
      
      // Skip predictive search API (returns 417) and go straight to regular search
      // This is faster and more reliable
      const routes = window.routes || {};
      const searchUrl = new URL(routes.search_url || '/search', window.location.origin);
      searchUrl.searchParams.set('q', query);
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('limit', '8'); // Limit to 8 for faster response
      
      console.log('🔍 Search URL:', searchUrl.toString());

      const response = await fetch(searchUrl, {
        signal: signal
      }).catch(err => {
        // Handle abort errors silently
        if (err.name === 'AbortError') {
          console.log('🔍 Search aborted (user is typing)');
          throw err; // Re-throw to be caught by outer catch
        }
        throw err;
      });

      if (!response.ok) {
        console.error('🔍 Search failed with status:', response.status);
        throw new Error(`Search failed: ${response.status}`);
      }

      const html = await response.text();
      console.log('🔍 Response length:', html.length);
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract products from search results page
      const products = this.extractProducts(doc);
      console.log('🔍 Extracted products:', products.length);
      
      // Try to get total results count
      const resultsCountEl = doc.querySelector('[data-results-count], .search-results-count, .results-count');
      let totalResults = products.length;
      if (resultsCountEl) {
        const countText = resultsCountEl.textContent.match(/\d+/);
        if (countText) totalResults = parseInt(countText[0], 10);
      }
      
      console.log('🔍 Final products:', products.length, 'Total:', totalResults);
      this.displayResults(products, query, totalResults);
    } catch (error) {
      // Silently ignore AbortError - it's expected when user types quickly
      if (error.name === 'AbortError') {
        console.log('🔍 Search aborted (user is typing)');
        return;
      }
      console.error('🔍 Search error:', error);
      this.showError();
    }
  }

  extractProductsFromSection(doc) {
    const products = [];
    const seenUrls = new Set(); // Track seen product URLs to avoid duplicates
    
    // Look for predictive search section
    const section = doc.querySelector('#shopify-section-predictive-search, .shopify-section[id*="predictive-search"], .predictive-search-empty-section');
    const searchArea = section || doc.body || doc;
    
    // Try multiple selectors to find product cards
    const productCards = searchArea.querySelectorAll('.resource-card, .predictive-search-results__card--product, [data-product-id], .product-card, .card-product, .card-product2, .grid__item, .card');
    
    productCards.forEach(card => {
      const link = card.querySelector('a[href*="/products/"]') || card.closest('a[href*="/products/"]');
      if (!link) return;

      // Get product title - prioritize specific title selectors
      let title = '';
      const titleSelectors = [
        '.card__heading a',
        '.card__main-link',
        '.resource-card__title',
        '.card-product__title',
        '.card-product2__title',
        'h3 a',
        'h4 a',
        '[data-product-title]',
        '.product-title',
        '.card__heading',
        'h3',
        'h4'
      ];
      
      for (const selector of titleSelectors) {
        const titleEl = card.querySelector(selector);
        if (titleEl) {
          title = titleEl.textContent?.trim();
          if (title && title !== 'Product' && title.length > 0) {
            break;
          }
        }
      }
      
      // Fallback to link text if no title found
      if (!title || title === 'Product') {
        title = link.textContent?.trim() || '';
        // Clean up title - remove price info if it got mixed in
        title = title.split('מחיר')[0].trim();
        if (!title || title.length < 2) {
          return; // Skip if we can't get a proper title
        }
      }
      
      // Get product image - look for featured image first
      let image = '';
      
      // Try multiple image selectors in order of preference
      const imageSelectors = [
        '.card__media img',
        '.card__inner .media img',
        '.media img',
        '.resource-card__image',
        '.card-product__image',
        'img'
      ];
      
      for (const selector of imageSelectors) {
        const imgEl = card.querySelector(selector);
        if (!imgEl) continue;
        
        // Helper function to extract URL from srcset
        const extractFromSrcset = (srcset) => {
          if (!srcset) return '';
          const parts = srcset.split(',');
          // Look for the largest image (usually 533w or 720w for product cards)
          for (let i = parts.length - 1; i >= 0; i--) {
            const part = parts[i].trim();
            const url = part.split(/\s+/)[0];
            if (url && url.includes('cdn.shopify.com') && !url.includes('data:image')) {
              return url;
            }
          }
          // If no Shopify CDN found, get the last (largest) one
          if (parts.length > 0) {
            const lastPart = parts[parts.length - 1].trim();
            return lastPart.split(/\s+/)[0];
          }
          return '';
        };
        
        // Try srcset first (usually has the best quality and proper Shopify URLs)
        const srcset = imgEl.getAttribute('srcset');
        if (srcset) {
          image = extractFromSrcset(srcset);
          if (image && image.includes('cdn.shopify.com')) {
            break;
          }
        }
        
        // Try src attribute (should have proper Shopify URL)
        const src = imgEl.getAttribute('src') || imgEl.src;
        if (src && src.includes('cdn.shopify.com') && !src.includes('data:image') && !src.includes('placeholder') && !src.includes('base64')) {
          image = src;
          break;
        }
        
        // Try data-src for lazy loading
        const dataSrc = imgEl.getAttribute('data-src') || imgEl.getAttribute('data-lazy-src') || imgEl.getAttribute('data-original');
        if (dataSrc && dataSrc.includes('cdn.shopify.com') && !dataSrc.includes('data:image') && !dataSrc.includes('placeholder')) {
          image = dataSrc;
          break;
        }
        
        // If we got an image from srcset but it's not Shopify CDN, use it anyway
        if (image && !image.includes('cdn.shopify.com')) {
          // Check if it's a valid URL
          if (image.startsWith('http') || image.startsWith('//') || image.startsWith('/')) {
            break;
          }
        }
      }
      
      // If still no image, try to find it in nested elements
      if (!image) {
        const mediaWrapper = card.querySelector('.card__media, .card__inner, .media, .resource-card__media');
        if (mediaWrapper) {
          const imgEl = mediaWrapper.querySelector('img');
          if (imgEl) {
            const srcset = imgEl.getAttribute('srcset');
            if (srcset) {
              const parts = srcset.split(',');
              if (parts.length > 0) {
                image = parts[parts.length - 1].trim().split(/\s+/)[0];
              }
            }
            if (!image) {
              image = imgEl.getAttribute('src') || imgEl.src || imgEl.getAttribute('data-src') || '';
            }
          }
        }
      }
      
      // Get price - prioritize sale price, then regular price
      let price = '';
      
      // First, check if there's a sale price (inside .price__sale)
      const priceSaleContainer = card.querySelector('.price__sale');
      if (priceSaleContainer) {
        // Get the sale price (not the crossed-out regular price)
        const salePriceEl = priceSaleContainer.querySelector('.price-item--sale, .price-item--last:not(.unit-price .price-item--last)');
        if (salePriceEl) {
          price = salePriceEl.textContent?.trim() || '';
        }
      }
      
      // If no sale price found, get regular price
      if (!price) {
        const priceRegularContainer = card.querySelector('.price__regular');
        if (priceRegularContainer) {
          const regularPriceEl = priceRegularContainer.querySelector('.price-item--regular');
          if (regularPriceEl) {
            price = regularPriceEl.textContent?.trim() || '';
          }
        }
      }
      
      // Fallback: try any price-item that's not in a unit-price
      if (!price) {
        const anyPriceEl = card.querySelector('.price-item:not(.unit-price .price-item)');
        if (anyPriceEl) {
          price = anyPriceEl.textContent?.trim() || '';
        }
      }
      
      // Clean price - remove extra text
      if (price) {
        // Remove Hebrew labels and keep only the price
        price = price.replace(/מחיר\s*(רגיל|מבצע|ליחידה|לכל)/gi, '').trim();
        price = price.replace(/\s*\/\s*.*$/, '').trim(); // Remove unit price info
        // Remove any crossed-out price text (strikethrough)
        price = price.replace(/<s>.*?<\/s>/gi, '').trim();
        // Extract just the price (number + currency)
        const priceMatch = price.match(/[\d.,]+\s*[₪$€£]?/);
        if (priceMatch) {
          price = priceMatch[0].trim();
        }
      }

      const fixedImage = this.fixImageUrl(image);
      
      // Normalize URL to avoid duplicates (remove query params)
      const normalizedUrl = link.href.split('?')[0];
      
      // Skip if we've already seen this product
      if (seenUrls.has(normalizedUrl)) {
        return;
      }
      seenUrls.add(normalizedUrl);
      
      products.push({
        title,
        url: link.href,
        image: fixedImage,
        price
      });
    });

    console.log('🔍 Extracted products:', products.length, products.map(p => ({ title: p.title, hasImage: !!p.image, price: p.price })));
    return products;
  }

  fixImageUrl(url) {
    if (!url) return '';
    
    // Skip data URIs and placeholders
    if (url.startsWith('data:') || url.includes('placeholder') || url.includes('base64') || url.includes('R0lGODlhAQAB')) {
      return '';
    }
    
    // Convert relative URLs to absolute
    if (url.startsWith('//')) {
      url = window.location.protocol + url;
    } else if (url.startsWith('/')) {
      // Relative URLs starting with / are valid - convert to absolute
      // This includes /cdn/shop/ paths which are valid Shopify asset paths
      url = window.location.origin + url;
    } else if (!url.startsWith('http')) {
      url = window.location.origin + '/' + url.replace(/^\//, '');
    }
    
    // Now that we have an absolute URL, check if it's a valid Shopify asset URL
    // Valid patterns:
    // - cdn.shopify.com (production CDN)
    // - /cdn/shop/ (local dev or Shopify asset path)
    const isShopifyUrl = url.includes('cdn.shopify.com') || url.includes('/cdn/shop/');
    
    if (!isShopifyUrl) {
      console.log('🔍 Not a Shopify URL, skipping:', url);
      return '';
    }
    
    // If it's a Shopify CDN URL (production), ensure proper sizing
    if (url.includes('cdn.shopify.com')) {
      // Remove existing size parameters and query strings
      url = url.replace(/_[0-9]+x[0-9]+\./, '.').replace(/\?.*$/, '');
      // Add width parameter for proper sizing (400px is good for product cards)
      url += (url.includes('?') ? '&' : '?') + 'width=400&height=500&crop=center';
      return url;
    }
    
    // For local dev URLs with /cdn/shop/, add sizing parameters if not already present
    if (url.includes('/cdn/shop/')) {
      // Check if it already has query parameters
      if (url.includes('?')) {
        // Add width/height if not already present
        if (!url.includes('width=')) {
          url += '&width=400&height=500&crop=center';
        }
      } else {
        url += '?width=400&height=500&crop=center';
      }
      return url;
    }
    
    return url;
  }

  parseJsonProducts(data) {
    const products = [];
    
    if (data.resources && data.resources.results) {
      // Shopify predictive search JSON format
      data.resources.results.forEach(item => {
        if (item.resource_type === 'product') {
          products.push({
            title: item.title,
            url: item.url,
            image: item.image || item.featured_image || '',
            price: item.price ? this.formatPrice(item.price) : ''
          });
        }
      });
    } else if (data.resources && data.resources.products) {
      // Alternative format
      data.resources.products.forEach(product => {
        products.push({
          title: product.title,
          url: product.url,
          image: product.featured_image?.url || product.image || '',
          price: product.price ? this.formatPrice(product.price) : ''
        });
      });
    }

    return products;
  }

  extractProductFromPage(doc, productUrl) {
    // Extract product data from a product page
    const product = {
      title: '',
      url: productUrl,
      image: '',
      price: ''
    };
    
    // Get product title
    const titleSelectors = [
      'h1.product__title',
      '.product__title h1',
      'h1[data-product-title]',
      '.product-title',
      'h1'
    ];
    
    for (const selector of titleSelectors) {
      const titleEl = doc.querySelector(selector);
      if (titleEl && titleEl.textContent.trim()) {
        product.title = titleEl.textContent.trim();
        break;
      }
    }
    
    // Get product image
    const imageSelectors = [
      '.product__media img',
      '.product__photo img',
      '[data-product-image] img',
      '.product-image img',
      'img[data-product-image]'
    ];
    
    for (const selector of imageSelectors) {
      const imgEl = doc.querySelector(selector);
      if (imgEl) {
        const srcset = imgEl.getAttribute('srcset');
        if (srcset) {
          const parts = srcset.split(',');
          if (parts.length > 0) {
            product.image = parts[parts.length - 1].trim().split(/\s+/)[0];
          }
        }
        if (!product.image) {
          product.image = imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || '';
        }
        if (product.image) break;
      }
    }
    
    // Get product price
    const priceSelectors = [
      '.price__sale .price-item--sale',
      '.price-item--sale',
      '.product__price .price',
      '.price'
    ];
    
    for (const selector of priceSelectors) {
      const priceEl = doc.querySelector(selector);
      if (priceEl && priceEl.textContent.trim()) {
        product.price = priceEl.textContent.trim().replace(/מחיר רגיל|מחיר מבצע|מחיר ליחידה/g, '').replace(/\s+/g, ' ').trim();
        break;
      }
    }
    
    return product;
  }

  extractProducts(doc) {
    const products = [];
    const seenUrls = new Set(); // Track seen product URLs to avoid duplicates
    
    // Try to find product elements in various formats
    const productElements = doc.querySelectorAll('[data-product-id], .product-card, .grid__item, .card-product, .product-grid__item, .card, .card-wrapper');
    
    productElements.forEach(element => {
      const link = element.querySelector('a[href*="/products/"]') || element.closest('a[href*="/products/"]');
      if (!link) return;

      // Get product title
      let title = '';
      const titleSelectors = [
        '.card__heading a',
        '.card__main-link',
        '.card-product__title',
        'h3 a',
        'h4 a',
        '[data-product-title]',
        '.product-title',
        'h3',
        'h4'
      ];
      
      for (const selector of titleSelectors) {
        const titleEl = element.querySelector(selector);
        if (titleEl) {
          title = titleEl.textContent?.trim();
          if (title && title !== 'Product' && title.length > 0) {
            break;
          }
        }
      }
      
      // Fallback to link text
      if (!title || title === 'Product') {
        title = link.textContent?.trim() || '';
        title = title.split('מחיר')[0].trim();
        if (!title || title.length < 2) {
          return;
        }
      }
      
      // Get image - improved extraction
      let image = '';
      const imageSelectors = [
        '.card__media img',
        '.card__inner .media img',
        '.media img',
        '.resource-card__image',
        '.card-product__image',
        'img'
      ];
      
      // Helper function to extract URL from srcset
      const extractFromSrcset = (srcset) => {
        if (!srcset) return '';
        const parts = srcset.split(',');
        // Look for the largest image (usually 533w or 720w for product cards)
        for (let i = parts.length - 1; i >= 0; i--) {
          const part = parts[i].trim();
          const url = part.split(/\s+/)[0];
          if (url && url.includes('cdn.shopify.com') && !url.includes('data:image')) {
            return url;
          }
        }
        // If no Shopify CDN found, get the last (largest) one
        if (parts.length > 0) {
          const lastPart = parts[parts.length - 1].trim();
          return lastPart.split(/\s+/)[0];
        }
        return '';
      };
      
      for (const selector of imageSelectors) {
        const imgEl = element.querySelector(selector);
        if (!imgEl) continue;
        
        // Try srcset first (usually has the best quality and proper Shopify URLs)
        const srcset = imgEl.getAttribute('srcset');
        if (srcset) {
          image = extractFromSrcset(srcset);
          if (image && image.includes('cdn.shopify.com')) {
            break;
          }
        }
        
        // Try src attribute (should have proper Shopify URL)
        const src = imgEl.getAttribute('src') || imgEl.src;
        if (src && src.includes('cdn.shopify.com') && !src.includes('data:image') && !src.includes('placeholder') && !src.includes('base64')) {
          image = src;
          break;
        }
        
        // Try data-src for lazy loading
        const dataSrc = imgEl.getAttribute('data-src') || imgEl.getAttribute('data-lazy-src') || imgEl.getAttribute('data-original');
        if (dataSrc && dataSrc.includes('cdn.shopify.com') && !dataSrc.includes('data:image') && !dataSrc.includes('placeholder')) {
          image = dataSrc;
          break;
        }
        
        // If we got an image from srcset but it's not Shopify CDN, use it anyway
        if (image && !image.includes('cdn.shopify.com')) {
          // Check if it's a valid URL
          if (image.startsWith('http') || image.startsWith('//') || image.startsWith('/')) {
            break;
          }
        }
      }
      
      // If still no image, try to find it in nested elements
      if (!image) {
        const mediaWrapper = element.querySelector('.card__media, .card__inner, .media, .resource-card__media');
        if (mediaWrapper) {
          const imgEl = mediaWrapper.querySelector('img');
          if (imgEl) {
            const srcset = imgEl.getAttribute('srcset');
            if (srcset) {
              image = extractFromSrcset(srcset);
            }
            if (!image) {
              image = imgEl.getAttribute('src') || imgEl.src || imgEl.getAttribute('data-src') || '';
            }
          }
        }
      }
      
      // Get price - prioritize sale price, then regular price
      let price = '';
      
      // First, check if there's a sale price (inside .price__sale)
      const priceSaleContainer = element.querySelector('.price__sale');
      if (priceSaleContainer) {
        // Get the sale price (not the crossed-out regular price)
        const salePriceEl = priceSaleContainer.querySelector('.price-item--sale, .price-item--last:not(.unit-price .price-item--last)');
        if (salePriceEl) {
          price = salePriceEl.textContent?.trim() || '';
        }
      }
      
      // If no sale price found, get regular price
      if (!price) {
        const priceRegularContainer = element.querySelector('.price__regular');
        if (priceRegularContainer) {
          const regularPriceEl = priceRegularContainer.querySelector('.price-item--regular');
          if (regularPriceEl) {
            price = regularPriceEl.textContent?.trim() || '';
          }
        }
      }
      
      // Fallback: try any price-item that's not in a unit-price
      if (!price) {
        const anyPriceEl = element.querySelector('.price-item:not(.unit-price .price-item)');
        if (anyPriceEl) {
          price = anyPriceEl.textContent?.trim() || '';
        }
      }
      
      // Clean price
      if (price) {
        price = price.replace(/מחיר\s*(רגיל|מבצע|ליחידה|לכל)/gi, '').trim();
        price = price.replace(/\s*\/\s*.*$/, '').trim();
        // Remove any crossed-out price text (strikethrough)
        price = price.replace(/<s>.*?<\/s>/gi, '').trim();
        const priceMatch = price.match(/[\d.,]+\s*[₪$€£]?/);
        if (priceMatch) {
          price = priceMatch[0].trim();
        }
      }

      const fixedImage = this.fixImageUrl(image);
      
      // Normalize URL to avoid duplicates (remove query params)
      const normalizedUrl = link.href.split('?')[0];
      
      // Skip if we've already seen this product
      if (seenUrls.has(normalizedUrl)) {
        return;
      }
      seenUrls.add(normalizedUrl);
      
      products.push({
        title,
        url: link.href,
        image: fixedImage,
        price
      });
    });

    console.log('🔍 Extracted products (fallback):', products.length, products.map(p => ({ title: p.title, hasImage: !!p.image, price: p.price })));

    // If no products found, try parsing JSON from script tags
    if (products.length === 0) {
      const scripts = doc.querySelectorAll('script[type="application/json"]');
      scripts.forEach(script => {
        try {
          const data = JSON.parse(script.textContent);
          if (data.resources?.products) {
            data.resources.products.forEach(product => {
              const finalPrice = product.compare_at_price && product.compare_at_price > product.price 
                ? product.price 
                : (product.price || product.compare_at_price);
              
              products.push({
                title: product.title,
                url: product.url,
                image: this.fixImageUrl(product.featured_image?.url || product.image || ''),
                price: finalPrice ? this.formatPrice(finalPrice) : ''
              });
            });
          }
        } catch (e) {
          // Ignore parse errors
        }
      });
    }

    return products;
  }

  formatPrice(price) {
    if (typeof price === 'number') {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: 'USD'
      }).format(price / 100);
    }
    return price;
  }

  displayResults(products, query, totalResults = null) {
    this.hideLoading();

    // Don't show stale results if input has changed
    const currentInput = this.input?.value.trim() || '';
    if (currentInput !== query) {
      console.log('🔍 Skipping stale results for:', query, 'current input:', currentInput);
      return;
    }

    if (products.length === 0) {
      this.showNoResults(query);
      return;
    }

    // Filter out invalid products
    const validProducts = products.filter(p =>
      p.title &&
      p.title !== 'Product' &&
      p.title.length > 2
    );

    if (validProducts.length === 0) {
      this.showNoResults(query);
      return;
    }

    // Limit to 8 products for display
    const displayProducts = validProducts.slice(0, 8);
    this.totalResults = totalResults || validProducts.length;

    const html = displayProducts.map(product => `
      <a href="${product.url}" class="clean-search-product-card">
        <div class="clean-search-product-card__image-wrapper">
          ${product.image ? `<img src="${this.fixImageUrl(product.image)}" alt="${this.escapeHtml(product.title)}" class="clean-search-product-card__image" loading="lazy" onerror="this.style.display='none';">` : '<div class="clean-search-product-card__image-placeholder"></div>'}
        </div>
        <h3 class="clean-search-product-card__title">${this.escapeHtml(product.title)}</h3>
        ${product.price ? `<div class="clean-search-product-card__price">${this.escapeHtml(product.price)}</div>` : ''}
      </a>
    `).join('');

    if (this.resultsGrid) {
      this.resultsGrid.innerHTML = html;
      this.resultsGrid.hidden = false;
    }

    // IMPORTANT: Hide empty state FIRST, then show results
    // Only use hidden attribute to avoid visual jumps
    if (this.emptyContainer) {
      this.emptyContainer.hidden = true;
    }
    if (this.recentlyViewedContainer) {
      this.recentlyViewedContainer.hidden = true;
    }
    if (this.recommendedProducts?.parentElement) {
      this.recommendedProducts.parentElement.hidden = true;
    }

    // Hide any "no results" message and show results title
    if (this.resultsContainer) {
      const noResultsEl = this.resultsContainer.querySelector('.clean-search-no-results');
      if (noResultsEl) noResultsEl.hidden = true;

      const resultsTitle = this.resultsContainer.querySelector('.clean-search-results__title');
      if (resultsTitle) resultsTitle.hidden = false;

      this.resultsContainer.hidden = false;
    }

    // Show "View all" button if there are more results
    if (this.viewAllContainer && this.viewAllLink && this.totalResults > 8) {
      const routes = window.routes || {};
      const searchUrl = new URL(routes.search_url || '/search', window.location.origin);
      searchUrl.searchParams.set('q', query);
      this.viewAllLink.href = searchUrl.toString();
      this.viewAllContainer.hidden = false;
    } else if (this.viewAllContainer) {
      this.viewAllContainer.hidden = true;
    }
  }

  async loadRecommendedProducts() {
    console.log('🔍 loadRecommendedProducts() called');
    try {
      if (!this.recommendedProducts) {
        console.warn('🔍 Recommended products container not found!');
        return;
      }

      // Check if there are recently viewed products in storage FIRST
      // If yes, hide recommended section immediately and only show recently viewed
      const viewedProducts = RecentlyViewed.getProducts();
      const recommendedSection = this.recommendedProducts?.closest('.clean-search-recommended');
      const recommendedTitle = recommendedSection?.querySelector('[data-recommended-title], .clean-search-recommended__title');

      // ALWAYS hide the title initially - it should only show after products are loaded
      if (recommendedTitle) {
        recommendedTitle.hidden = true;
      }

      // Hide toggle button initially
      if (this.toggleButton) {
        this.toggleButton.hidden = true;
      }

      if (viewedProducts.length > 0) {
        console.log('🔍 Recently viewed products exist in storage, hiding recommended products');
        // Hide the entire recommended section AND its title
        if (recommendedSection) {
          recommendedSection.hidden = true;
          recommendedSection.style.display = 'none';
        }
        // Reset to show recently viewed by default
        this.showingRecentlyViewed = true;
        if (this.toggleText) {
          this.toggleText.textContent = 'מומלצי החודש';
        }
        // Load and show recently viewed products (this will also show the toggle button)
        await this.loadRecentlyViewedProducts();

        // Pre-load recommended products in the background for quick toggle
        this.loadRecommendedProductsForToggle();
        return;
      }

      // No recently viewed products - prepare to show recommended section
      // Hide toggle button (no toggle needed when only showing recommended)
      if (this.toggleButton) {
        this.toggleButton.hidden = true;
      }
      // But keep title hidden until products are actually loaded
      console.log('🔍 No recently viewed products, loading recommended products');
      if (recommendedSection) {
        recommendedSection.hidden = false;
        recommendedSection.style.display = '';
      }
      if (this.recentlyViewedContainer) {
        this.recentlyViewedContainer.hidden = true;
      }

      // Get only recommended products (4 max)
      const recommended = await this.getRecommendedProducts();

      if (recommended.length > 0) {
        // Filter out products without proper titles or images
        const validProducts = recommended.filter(p =>
          p.title &&
          p.title !== 'Product' &&
          p.title.length > 2 &&
          p.image &&
          p.image.length > 0
        );

        // Shuffle products to show variety each time (Fisher-Yates shuffle)
        for (let i = validProducts.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [validProducts[i], validProducts[j]] = [validProducts[j], validProducts[i]];
        }

        // Limit to 4 products
        const displayProducts = validProducts.slice(0, 4);

        if (displayProducts.length > 0) {
          // Cache the recommended products
          this.recommendedProductsCache = displayProducts;
          this.recommendedProductsLoaded = true;

          const html = displayProducts.map(product => `
            <a href="${product.url}" class="clean-search-product-card">
              <div class="clean-search-product-card__image-wrapper">
                ${product.image ? `<img src="${this.fixImageUrl(product.image)}" alt="${this.escapeHtml(product.title)}" class="clean-search-product-card__image" loading="lazy" onerror="this.style.display='none';">` : '<div class="clean-search-product-card__image-placeholder"></div>'}
              </div>
              <h3 class="clean-search-product-card__title">${this.escapeHtml(product.title)}</h3>
              ${product.price ? `<div class="clean-search-product-card__price">${this.escapeHtml(product.price)}</div>` : ''}
            </a>
          `).join('');

          this.recommendedProducts.innerHTML = html;

          // Show recommended section and title only when we have products
          // Only show if search is not active (prevents race condition)
          if (!this.isSearchActive) {
            if (recommendedSection) {
              recommendedSection.hidden = false;
            }
            if (recommendedTitle) {
              recommendedTitle.hidden = false;
            }

            // Show empty container (which contains recommended products)
            this.emptyContainer.hidden = false;
          }
        } else {
          // Hide recommended section title if no products
          if (recommendedTitle) {
            recommendedTitle.hidden = true;
          }

          // Hide empty container if no recommended products AND no recently viewed
          if (!this.recentlyViewedContainer || this.recentlyViewedContainer.hidden) {
            this.emptyContainer.hidden = true;
          }
        }
      } else {
        // Hide recommended section title if no products
        if (recommendedTitle) {
          recommendedTitle.hidden = true;
        }

        // Hide empty container if no recommended products AND no recently viewed
        if (!this.recentlyViewedContainer || this.recentlyViewedContainer.hidden) {
          this.emptyContainer.hidden = true;
        }
      }
    } catch (error) {
      console.error('Error loading recommended products:', error);
      if (this.emptyContainer && (!this.recentlyViewedContainer || this.recentlyViewedContainer.hidden)) {
        this.emptyContainer.hidden = true;
      }
    }
  }

  async loadRecentlyViewedProducts() {
    try {
      if (!this.recentlyViewedContainer || !this.recentlyViewedProducts) {
        console.warn('📦 Cannot load recently viewed: missing container or products element');
        console.warn('📦 Container:', this.recentlyViewedContainer, 'Products:', this.recentlyViewedProducts);
        return;
      }
      
      // Prevent concurrent requests
      if (this.recentlyViewedLoading) {
        console.log('📦 Already loading recently viewed products, skipping...');
        return;
      }

      const productIds = RecentlyViewed.getProducts();
      console.log('📦 Loading recently viewed products, count:', productIds.length, 'Handles:', productIds);
      console.log('📦 Container current state - hidden:', this.recentlyViewedContainer.hidden);
      
      // If no products, hide the container immediately and return
      if (productIds.length === 0) {
        console.log('📦 No products found in storage, hiding recently viewed section');
        console.log('📦 Tip: Visit some product pages to see recently viewed products here');
        this.recentlyViewedContainer.hidden = true;
        if (this.recentlyViewedProducts) {
          this.recentlyViewedProducts.innerHTML = '';
        }
        this.recentlyViewedCache = null;
        return;
      }
      
      // Check cache first
      const now = Date.now();
      if (this.recentlyViewedCache && (now - this.recentlyViewedCacheTime) < this.CACHE_DURATION) {
        console.log('📦 Using cached recently viewed products, count:', this.recentlyViewedCache.length);
        this.displayRecentlyViewedProducts(this.recentlyViewedCache);
        return;
      }
      
      console.log('📦 Cache expired or empty, fetching fresh products...');
      
      // We have products to load - start loading
      // Container visibility will be set when products are loaded (show if successful, hide if failed)
      this.recentlyViewedLoading = true;

      // Since we now store handles, productIds are actually handles
      const handles = productIds.slice(0, 4);
      console.log('📦 Fetching products for handles:', handles);
      
      // Fetch products individually to ensure we get all of them
      // Shopify search with OR might not work reliably
      let products = [];
      
      // Fetch each product individually by visiting its product page
      // This is more reliable than search API
      const fetchPromises = handles.map(async (handle) => {
        try {
          const productUrl = new URL(`/products/${handle}`, window.location.origin);
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout per product
          
          const response = await fetch(productUrl, {
            method: 'GET',
            headers: {
              'Accept': 'text/html',
            },
            signal: controller.signal
          }).catch(err => {
            // Silently handle fetch errors (network issues, CORS, etc.)
            if (err.name === 'AbortError') {
              throw err; // Re-throw abort to be handled below
            }
            // For other fetch errors, return null (product won't be shown)
            console.warn('📦 Network error fetching product', handle);
            return null;
          });
          
          clearTimeout(timeoutId);
          
          // If fetch returned null (error), return null
          if (!response) return null;
          
          if (response.ok) {
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            // Extract product data from product page
            // Hidden now, even if it was viewed before it was hidden.
            if (doc.querySelector('meta[name="product-hidden"]')) {
              console.log('\ud83d\udce6 Skipping a hidden product in recently viewed:', handle);
              return null;
            }

            const product = this.extractProductFromPage(doc, productUrl.toString());
            if (product && product.title && product.url) {
              console.log('📦 Fetched product:', product.title, product.url);
              return product;
            }
          } else {
            console.warn('📦 Product page returned status', response.status, 'for', handle);
          }
        } catch (error) {
          // Silently handle abort errors (timeouts are expected)
          if (error.name === 'AbortError') {
            console.log('📦 Timeout fetching product', handle);
          } else {
            console.warn('📦 Error fetching product', handle, ':', error.message);
          }
        }
        return null;
      });
      
      try {
        const fetchedProducts = await Promise.all(fetchPromises);
        products = fetchedProducts.filter(p => p !== null);
        console.log('📦 Successfully fetched', products.length, 'out of', handles.length, 'products');
        if (products.length > 0) {
          console.log('📦 Products:', products.map(p => ({ title: p.title, url: p.url })));
        }
      } catch (error) {
        // This should never happen since we handle errors in individual promises
        // But just in case, handle it gracefully
        console.warn('📦 Unexpected error fetching products:', error.message);
        products = [];
      } finally {
        // Always reset loading flag
        this.recentlyViewedLoading = false;
      }
      
      // Filter and limit to 4, remove duplicates
      const seenUrls = new Set();
      const validProducts = products
        .filter(p => {
          if (!p || !p.title || p.title === 'Product' || p.title.length < 2 || !p.image || p.image.length === 0) {
            return false;
          }
          const normalizedUrl = p.url.split('?')[0];
          if (seenUrls.has(normalizedUrl)) {
            return false;
          }
          seenUrls.add(normalizedUrl);
          return true;
        })
        .slice(0, 4);

      // Only show the container if we have valid products to display
      if (validProducts.length > 0) {
        // Cache the results
        this.recentlyViewedCache = validProducts;
        this.recentlyViewedCacheTime = Date.now();

        this.displayRecentlyViewedProducts(validProducts);
        console.log('✅ Showing recently viewed products:', validProducts.length, validProducts.map(p => p.title));
      } else {
        // Hide if no valid products found (even if IDs exist in storage, they might be invalid)
        this.recentlyViewedContainer.hidden = true;
        if (this.recentlyViewedProducts) {
          this.recentlyViewedProducts.innerHTML = '';
        }
        this.recentlyViewedCache = null;
        console.log('❌ No valid products found, hiding recently viewed section. Tried to fetch:', productIds.length, 'products, got:', products.length, 'valid products');
      }
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
      // On error, check if we have products in storage
      const productIds = RecentlyViewed.getProducts();
      if (productIds.length === 0) {
        // Only hide if we truly have no products
        if (this.recentlyViewedContainer) {
          this.recentlyViewedContainer.hidden = true;
        }
      } else {
        // If we have products in storage but fetch failed, hide container (will retry on next open)
        // Don't show empty container
        this.recentlyViewedContainer.hidden = true;
        if (this.recentlyViewedProducts) {
          this.recentlyViewedProducts.innerHTML = '';
        }
      }
    }
  }
  
  displayRecentlyViewedProducts(products) {
    if (!this.recentlyViewedProducts || !this.recentlyViewedContainer) {
      console.warn('📦 Cannot display products: missing container or products element');
      console.warn('📦 Container:', this.recentlyViewedContainer, 'Products:', this.recentlyViewedProducts);
      return;
    }

    if (!products || products.length === 0) {
      console.warn('📦 Cannot display: no products provided');
      this.recentlyViewedContainer.hidden = true;
      // Hide toggle button if no recently viewed products
      if (this.toggleButton) {
        this.toggleButton.hidden = true;
      }
      return;
    }

    console.log('📦 Displaying', products.length, 'recently viewed products');
    console.log('📦 Product titles:', products.map(p => p.title));

    const productsHtml = products.map(product => `
      <a href="${product.url}" class="clean-search-product-card">
        <div class="clean-search-product-card__image-wrapper">
          ${product.image ? `<img src="${this.fixImageUrl(product.image)}" alt="${this.escapeHtml(product.title)}" class="clean-search-product-card__image" loading="lazy" onerror="this.style.display='none';">` : '<div class="clean-search-product-card__image-placeholder"></div>'}
        </div>
        <h3 class="clean-search-product-card__title">${this.escapeHtml(product.title)}</h3>
        ${product.price ? `<div class="clean-search-product-card__price">${this.escapeHtml(product.price)}</div>` : ''}
      </a>
    `).join('');

    this.recentlyViewedProducts.innerHTML = productsHtml;
    // Show container - we have products to display
    // Only show if search is not active (prevents race condition)
    if (!this.isSearchActive) {
      this.recentlyViewedContainer.hidden = false;
      // Show the toggle button since we have recently viewed products
      if (this.toggleButton) {
        this.toggleButton.hidden = false;
        console.log('🔄 Toggle button is now visible');
      }
      console.log('✅ Recently viewed container is now visible with', products.length, 'products');
    } else {
      console.log('🔍 Search is active, not showing recently viewed container');
    }
    console.log('✅ Container element:', this.recentlyViewedContainer);
    console.log('✅ Container hidden attribute:', this.recentlyViewedContainer.hidden);
  }

  async getRecommendedProducts() {
    try {
      // Get collection handle from data attribute (configurable in liquid file)
      const recommendedSection = this.recommendedProducts?.closest('.clean-search-recommended');
      const collectionHandle = recommendedSection?.dataset.recommendedCollection || 'all';

      console.log('🔍 Fetching recommended products from collection:', collectionHandle);

      // Fetch products from the configured collection
      const collectionUrl = new URL(`/collections/${collectionHandle}`, window.location.origin);
      collectionUrl.searchParams.set('limit', '4');

      const response = await fetch(collectionUrl);
      if (!response.ok) {
        console.warn('🔍 Collection not found, falling back to all products');
        // Fallback to all products if configured collection doesn't exist
        const fallbackUrl = new URL('/collections/all', window.location.origin);
        fallbackUrl.searchParams.set('limit', '4');
        const fallbackResponse = await fetch(fallbackUrl);
        if (!fallbackResponse.ok) return [];
        const html = await fallbackResponse.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        return this.extractProducts(doc).slice(0, 4);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const products = this.extractProducts(doc);

      // Remove duplicates and limit to 4
      const seenUrls = new Set();
      const uniqueProducts = products.filter(p => {
        if (!p || !p.url) return false;
        const normalizedUrl = p.url.split('?')[0];
        if (seenUrls.has(normalizedUrl)) return false;
        seenUrls.add(normalizedUrl);
        return true;
      });

      return uniqueProducts.slice(0, 4);
    } catch (error) {
      console.error('Error getting recommended products:', error);
      return [];
    }
  }

  showLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.hidden = false;
    }
    if (this.resultsContainer) {
      this.resultsContainer.hidden = true;
      // Clear any previous "no results" message to prevent stale text
      const noResultsEl = this.resultsContainer.querySelector('.clean-search-no-results');
      if (noResultsEl) {
        noResultsEl.innerHTML = '';
        noResultsEl.hidden = true;
      }
    }
    if (this.emptyContainer) {
      this.emptyContainer.hidden = true;
    }
    // Clear the last no results query
    this.lastNoResultsQuery = null;
  }

  hideLoading() {
    if (this.loadingIndicator) {
      this.loadingIndicator.hidden = true;
    }
  }

  showEmptyState() {
    console.log('🔍 showEmptyState() called');
    this.isSearchActive = false; // Mark search as inactive

    // Reset toggle state to show recently viewed by default
    this.showingRecentlyViewed = true;
    if (this.toggleText) {
      this.toggleText.textContent = 'מומלצי החודש';
    }
    // Reset the title text
    const recentlyViewedTitle = this.recentlyViewedContainer?.querySelector('.clean-search-recently-viewed__title h4');
    if (recentlyViewedTitle) {
      recentlyViewedTitle.textContent = 'מוצרים שנצפו לאחרונה';
    }
    // Make sure recently viewed products grid is visible (not hidden from previous toggle)
    if (this.recentlyViewedProducts) {
      this.recentlyViewedProducts.hidden = false;
    }

    // Hide search results first (only use hidden attribute)
    if (this.resultsContainer) {
      this.resultsContainer.hidden = true;
      // Clear any "no results" message
      const noResultsEl = this.resultsContainer.querySelector('.clean-search-no-results');
      if (noResultsEl) {
        noResultsEl.innerHTML = '';
        noResultsEl.hidden = true;
      }
    }
    if (this.viewAllContainer) {
      this.viewAllContainer.hidden = true;
    }

    // Clear search results grid to avoid showing old results
    if (this.resultsGrid) {
      this.resultsGrid.innerHTML = '';
    }

    // Clear the last no results query
    this.lastNoResultsQuery = null;

    // Show empty state (recently viewed + recommended)
    // But recently viewed will only show if it has products
    if (this.emptyContainer) {
      this.emptyContainer.hidden = false;
      console.log('🔍 Empty container is now visible');
    } else {
      console.warn('🔍 Empty container not found!');
    }

    // Always reload recently viewed first (it will show if there are products)
    // Then load recommended products
    // This ensures that if products were added after clearing, they will show
    console.log('🔍 Calling loadRecommendedProducts()...');
    this.loadRecommendedProducts();
  }

  showNoResults(query) {
    // Don't show stale "no results" if input has changed
    const currentInput = this.input?.value.trim() || '';
    if (currentInput !== query) {
      console.log('🔍 Skipping stale no-results message for:', query, 'current input:', currentInput);
      return;
    }

    // Store the current query to detect stale messages
    this.lastNoResultsQuery = query;

    // Hide empty state (recommended products)
    if (this.emptyContainer) {
      this.emptyContainer.hidden = true;
    }

    // Clear the results grid first
    if (this.resultsGrid) {
      this.resultsGrid.innerHTML = '';
    }

    // Show no results message in a dedicated element, not by replacing resultsContainer
    if (this.resultsContainer) {
      // Check if no-results element exists, if not create it
      let noResultsEl = this.resultsContainer.querySelector('.clean-search-no-results');
      if (!noResultsEl) {
        noResultsEl = document.createElement('div');
        noResultsEl.className = 'clean-search-no-results';
        this.resultsContainer.appendChild(noResultsEl);
      }
      noResultsEl.innerHTML = `<p>לא נמצאו תוצאות עבור "${this.escapeHtml(query)}"</p>`;
      noResultsEl.hidden = false;

      // Hide the results title and grid
      const resultsTitle = this.resultsContainer.querySelector('.clean-search-results__title');
      if (resultsTitle) resultsTitle.hidden = true;
      if (this.resultsGrid) this.resultsGrid.hidden = true;
      if (this.viewAllContainer) this.viewAllContainer.hidden = true;

      this.resultsContainer.hidden = false;
    }
  }

  showError() {
    this.hideLoading();
    if (this.resultsContainer) {
      this.resultsContainer.innerHTML = `
        <div class="clean-search-no-results">
          <p>Something went wrong. Please try again.</p>
        </div>
      `;
      this.resultsContainer.hidden = false;
    }
  }

  updateClearButton() {
    if (this.clearButton) {
      this.clearButton.hidden = !this.input?.value.trim();
    }
  }

  submitSearch() {
    const query = this.input?.value.trim();
    if (!query) return;

    const routes = window.routes || {};
    const url = new URL(routes.search_url || '/search', window.location.origin);
    url.searchParams.set('q', query);
    window.location.href = url.toString();
  }

  // textContent handles < and &, and leaves " and ' alone - which is
  // what matters here, because the result is used inside attributes.
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('🔍 DOM loaded, initializing CleanSearch...');
    window.cleanSearch = new CleanSearch();
    // Track current product if on product page
    RecentlyViewed.trackCurrentProduct();
    
    // Add manual test function for debugging
    window.testRecentlyViewed = function() {
      console.log('🧪 Testing recently viewed...');
      console.log('🧪 Products in storage:', RecentlyViewed.getProducts());
      console.log('🧪 Container:', window.cleanSearch?.recentlyViewedContainer);
      console.log('🧪 Container hidden:', window.cleanSearch?.recentlyViewedContainer?.hidden);
      if (window.cleanSearch) {
        window.cleanSearch.loadRecentlyViewedProducts();
      }
    };
    
    // Add manual add function for testing
    window.addTestProduct = function(handle) {
      console.log('🧪 Adding test product:', handle);
      RecentlyViewed.addProduct(handle || 'test-product');
      console.log('🧪 Products now:', RecentlyViewed.getProducts());
      if (window.cleanSearch) {
        window.cleanSearch.recentlyViewedCache = null;
        window.cleanSearch.loadRecentlyViewedProducts();
      }
    };
  });
} else {
  console.log('🔍 DOM already loaded, initializing CleanSearch...');
  window.cleanSearch = new CleanSearch();
  // Track current product if on product page
  RecentlyViewed.trackCurrentProduct();
  
  // Add manual test function for debugging
  window.testRecentlyViewed = function() {
    console.log('🧪 Testing recently viewed...');
    console.log('🧪 Products in storage:', RecentlyViewed.getProducts());
    console.log('🧪 Container:', window.cleanSearch?.recentlyViewedContainer);
    console.log('🧪 Container hidden:', window.cleanSearch?.recentlyViewedContainer?.hidden);
    if (window.cleanSearch) {
      window.cleanSearch.loadRecentlyViewedProducts();
    }
  };
  
  // Add manual add function for testing
  window.addTestProduct = function(handle) {
    console.log('🧪 Adding test product:', handle);
    RecentlyViewed.addProduct(handle || 'test-product');
    console.log('🧪 Products now:', RecentlyViewed.getProducts());
    if (window.cleanSearch) {
      window.cleanSearch.recentlyViewedCache = null;
      window.cleanSearch.loadRecentlyViewedProducts();
    }
  };
}

// Track product views when navigating (for SPA-like behavior)
// This runs separately to ensure it always tracks products
(function() {
  // Debounced product click tracking
  let clickTrackingTimeout = null;
  function trackProductClick(e) {
    // Debounce to prevent multiple rapid clicks
    if (clickTrackingTimeout) {
      clearTimeout(clickTrackingTimeout);
    }
    
    clickTrackingTimeout = setTimeout(() => {
      const productLink = e.target.closest('a[href*="/products/"]');
      if (productLink && !productLink.hasAttribute('data-no-track')) {
        const productMatch = productLink.href.match(/\/products\/([^\/\?]+)/);
        if (productMatch) {
          const productHandle = productMatch[1];
          
          // Add product immediately (before navigation)
          // addProduct will handle deduplication automatically
          RecentlyViewed.addProduct(productHandle);
          console.log('📦 Tracked product view from click:', productHandle);
          
          // Clear cache when new product is added
          if (window.cleanSearch && window.cleanSearch.recentlyViewedCache) {
            window.cleanSearch.recentlyViewedCache = null;
          }
        }
      }
    }, 300); // 300ms debounce
  }
  
  // Track on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      RecentlyViewed.trackCurrentProduct();
      document.addEventListener('click', trackProductClick);
    });
  } else {
    RecentlyViewed.trackCurrentProduct();
    document.addEventListener('click', trackProductClick);
  }
  
  // Also track when URL changes (for SPA navigation)
  let lastUrl = window.location.href;
  const urlCheckInterval = setInterval(() => {
    const currentUrl = window.location.href;
    if (currentUrl !== lastUrl) {
      lastUrl = currentUrl;
      console.log('📦 URL changed, tracking product:', currentUrl);
      RecentlyViewed.trackCurrentProduct();
      
      // Clear cache so new products show immediately
      if (window.cleanSearch) {
        window.cleanSearch.recentlyViewedCache = null;
        window.cleanSearch.recentlyViewedCacheTime = 0;
        console.log('📦 Cleared cache after URL change');
      }
    }
  }, 500);
  
  // Clean up interval when page unloads
  window.addEventListener('beforeunload', () => {
    clearInterval(urlCheckInterval);
  });
})();
